import InvalidArgumentError from "../../Errors/InvalidArgumentError";

/**
 * NHS: https://www.nhs.uk/conditions/high-blood-pressure/
 * NHS Blood Pressure Test: https://www.nhs.uk/tests-and-treatments/blood-pressure-test/
 * Blood Pressure UK: https://www.bloodpressureuk.org/
 * NHS Health Check Best Practice Guide (London): https://www.england.nhs.uk/london/wp-content/uploads/sites/8/2019/08/2.1.7-NHS-Health-Check-Best-Practice-Guidance-2017.pdf
 * https://www.bhf.org.uk/informationsupport/risk-factors/high-blood-pressure?utm_source=chatgpt.com#Blood_pressure_readings
 * https://www.nice.org.uk/guidance/ng136/chapter/Recommendations#diagnosing-hypertension
 * */
export default class NationalHealthServiceBP {
    /**
     * Constructor
     */
    constructor(systolic, diastolic) {
        this._validateValue(systolic);
        this._validateValue(diastolic);
        this._systolic = systolic;
        this._diastolic = diastolic;
    }

    // FIXME: Should this be the concern of the Guideline? Might be better suited to the Risk model
    static get RiskName() {
        return "BLOOD_PRESSURE";
    }

    // FIXME: Should this be the concern of the Guideline? Might be better suited to the Risk model
    static get GuidelineName() {
        return "NHS_BP";
    }

    /**
     * Validates the given systolic or diastolic value. It is intended to be a positive number
     *
     * @returns {boolean}
     * @private
     */
    _validateValue(reading) {
        if (!(typeof reading === "number") || Number.isNaN(reading) || reading < 0) {
            throw new InvalidArgumentError("Systolic and Diastolic must be a valid number greater than or equal to 0");
        }

        return true;
    }

    /**
     * Returns if the diastolic and systolic values are in the 'Optimal' range
     *
     * @returns {boolean}
     */
    isLow() {
        return this._systolic < 90 && this._diastolic < 60;
    }

    /**
     * Returns if the diastolic and systolic values are in the 'Normal' range
     *
     * @returns {boolean}
     */
    isNormal() {
        return (this._systolic >= 90 && this._systolic <= 119) || (this._diastolic >= 60 && this._diastolic <= 79);
    }

    /**
     * Returns if the diastolic and systolic values are in the 'High Normal' range
     *
     * @returns {boolean}
     */
    isHighNormal() {
        return (this._systolic >= 120 && this._systolic <= 139) || (this._diastolic >= 80 && this._diastolic <= 89);
    }

    /**
     * Returns if the diastolic and systolic values are in the 'Grade 1 (mild) hypertension' range
     *
     * @returns {boolean}
     */
    isHigh() {
        return (this._systolic >= 140 && this._systolic <= 179) || (this._diastolic >= 90 && this._diastolic <= 119);
    }

    /**
     * Returns if the diastolic and systolic values are in the 'Grade 3 (severe) hypertension' range
     *
     * @returns {boolean}
     */
    isSevere() {
        return this._systolic >= 180 || this._diastolic >= 120;
    }

    /**
     * Returns an object representing all labels corresponding to this guideline defination
     *
     * @returns {object}
     */
    static get labels() {
        return {
            low: "Low",
            normal: "Normal",
            highNormal: "High-Normal",
            high: "High",
            severe: "Severe"
        };
    }

    /**
     * Returns an object representing various data for each BP range
     *
     * @returns {object}
     */
    static get ranges() {
        return {
            low: {
                key: "low",
                label: "Low",
                i18nKey: "ranges.low",
                range: "< 90/60",
                riskLevel: 1 / 7
            },

            normal: {
                key: "normal",
                label: "Normal",
                i18nKey: "ranges.normal",
                range: "90/60+",
                riskLevel: 2 / 7
            },

            highNormal: {
                key: "highNormal",
                label: "High-Normal",
                i18nKey: "ranges.high_normal",
                range: "120/80+",
                riskLevel: 3 / 7
            },

            high: {
                key: "high",
                label: "High",
                i18nKey: "ranges.high",
                range: "140/90+",
                riskLevel: 4 / 7
            },

            severe: {
                key: "severe",
                label: "Severe",
                i18nKey: "ranges.severe",
                range: "180/120+",
                riskLevel: 6 / 7
            }
        };
    }

    /**
     * returns help information about the calculated range
     *
     * @returns {string}
     */
    get rangeInfo() {
        let info = "";
        switch (true) {
            case this.isSevere():
                info = NationalHealthServiceBP.ranges.severe.range;
                break;
            case this.isHigh():
                info = NationalHealthServiceBP.ranges.high.range;
                break;
            case this.isHighNormal():
                info = NationalHealthServiceBP.ranges.highNormal.range;
                break;
            case this.isNormal():
                info = NationalHealthServiceBP.ranges.normal.range;
                break;
            case this.isLow():
                info = NationalHealthServiceBP.ranges.low.range;
                break;
        }

        return info;
    }

    /**
     * returns normalised key about the calculated range
     *
     * @returns {string}
     */
    get key() {
        let key = "";
        switch (true) {
            case this.isSevere():
                key = NationalHealthServiceBP.ranges.severe.key;
                break;
            case this.isHigh():
                key = NationalHealthServiceBP.ranges.high.key;
                break;
            case this.isHighNormal():
                key = NationalHealthServiceBP.ranges.highNormal.key;
                break;
            case this.isNormal():
                key = NationalHealthServiceBP.ranges.normal.key;
                break;
            case this.isLow():
                key = NationalHealthServiceBP.ranges.low.key;
                break;
        }

        return key;
    }

    /**
     * returns label for the calculated range
     *
     * @returns {string}
     */
    get label() {
        let label = "";
        switch (true) {
            case this.isSevere():
                label = NationalHealthServiceBP.ranges.severe.label;
                break;
            case this.isHigh():
                label = NationalHealthServiceBP.ranges.high.label;
                break;
            case this.isHighNormal():
                label = NationalHealthServiceBP.ranges.highNormal.label;
                break;
            case this.isNormal():
                label = NationalHealthServiceBP.ranges.normal.label;
                break;
            case this.isLow():
                label = NationalHealthServiceBP.ranges.low.label;
                break;
        }

        return label;
    }

    /**
     * returns i18n translation key for the calculated range
     *
     * @returns {string}
     */
    get i18nKey() {
        let i18nKey = "";
        switch (true) {
            case this.isSevere():
                i18nKey = NationalHealthServiceBP.ranges.severe.i18nKey;
                break;
            case this.isHigh():
                i18nKey = NationalHealthServiceBP.ranges.high.i18nKey;
                break;
            case this.isHighNormal():
                i18nKey = NationalHealthServiceBP.ranges.highNormal.i18nKey;
                break;
            case this.isNormal():
                i18nKey = NationalHealthServiceBP.ranges.normal.i18nKey;
                break;
            case this.isLow():
                i18nKey = NationalHealthServiceBP.ranges.low.i18nKey;
                break;
        }

        return i18nKey;
    }

    /**
     * Calculates a risk level number between 0 - 1 for each rating
     *
     * @return {number}
     */
    get riskLevel() {
        let totalRiskLevel = 5,
            riskLevel = 0;
        switch (true) {
            case this.isSevere():
                riskLevel = 5 / (totalRiskLevel + 1);
                break;
            case this.isHigh():
                riskLevel = 4 / (totalRiskLevel + 1);
                break;
            case this.isHighNormal():
                riskLevel = 3 / (totalRiskLevel + 1);
                break;
            case this.isNormal():
                riskLevel = 2 / (totalRiskLevel + 1);
                break;
            case this.isLow():
                riskLevel = 1 / (totalRiskLevel + 1);
                break;
        }

        return riskLevel;
    }

    get isAtRisk() {
        return this.isHigh() || this.isSevere();
    }
}
